import { useMemo } from "react";
import { canUseFilter } from "constants";
import { isEmpty } from "utils";

/**
 * Custom hook to manage advanced filters logic
 *
 * @param {Object} selected - Selected filters state
 * @param {Array} initialStickyFilterKeys - Initial sticky filter keys
 * @param {Object} filterConfigMap - Filter configuration map
 * @param {Function} setSelected - Function to update selected filters
 * @param {Array} roles - User roles for filter access control
 * @returns {Object} Filter management utilities and computed values
 */
const useAdvancedFilters = ({ selected, initialStickyFilterKeys = [], filterConfigMap, setSelected, roles }) => {
  const { advancedFilters } = selected;

  const enabledFiltersFromUrl = useMemo(
    () =>
      Object.entries(advancedFilters || {}).reduce(
        (acc, [key, value]) => ({ ...acc, ...(value?.enabled ? { [key]: value } : {}) }),
        {}
      ),
    [advancedFilters]
  );

  const stickyFilterKeys = [...initialStickyFilterKeys, ...Object.keys(enabledFiltersFromUrl)];

  const filterConfig = Object.keys(filterConfigMap)
    .filter((key) => canUseFilter(filterConfigMap, key, roles))
    .reduce((acc, key) => ({ ...acc, [key]: filterConfigMap[key] }), {});

  const stickyFilters = useMemo(
    () =>
      Object.keys(filterConfig).reduce(
        (acc, key) => ({ ...acc, ...(stickyFilterKeys.includes(key) ? { [key]: filterConfig[key] } : {}) }),
        {}
      ),
    [stickyFilterKeys, filterConfig]
  );

  const operators = Object.keys(advancedFilters || {}).reduce(
    (acc, key) => ({ ...acc, [key]: advancedFilters[key]?.operator }),
    {}
  );

  const handleToggle = (name, value) => {
    const key = name?.split("[enabled]")[0];
    if (!key) return;

    setSelected((prev) => {
      const { advancedFilters, ...rest } = prev;
      const updatedFilters = { ...advancedFilters };

      updatedFilters[key] = {
        ...updatedFilters[key],
        enabled: value,
      };

      return { ...rest, advancedFilters: updatedFilters };
    });
  };

  const handleOperatorChange = (name, value) => {
    const key = name?.split("[operator]")[0];
    if (!key) return;

    setSelected((prev) => {
      const { advancedFilters, ...rest } = prev;
      const updatedFilters = { ...advancedFilters };

      updatedFilters[key] = {
        ...updatedFilters[key],
        operator: value,
      };

      return { ...rest, advancedFilters: updatedFilters };
    });
  };

  const handleChange = (data) => {
    setSelected((prev) => {
      const { advancedFilters, ...rest } = prev;
      const updatedFilters = { ...advancedFilters };

      Object.keys(data).forEach((key) => {
        updatedFilters[key] = {
          enabled: !isEmpty(data[key]?.value) ? updatedFilters[key]?.enabled ?? true : updatedFilters[key]?.enabled,
          operator: !!updatedFilters[key]?.operator,
          value: data[key]?.value,
        };
      });

      return { ...rest, advancedFilters: updatedFilters };
    });
  };

  return {
    enabledFiltersFromUrl,
    stickyFilterKeys,
    filterConfig,
    stickyFilters,
    operators,
    handleToggle,
    handleOperatorChange,
    handleChange,
  };
};

export default useAdvancedFilters;
